import { useCountByStatus } from "@/api/bibliography/bibliography";
import { readTablePreferences } from "@/hooks/useTablePreferences";
import { PagingParameters } from "@/api/dtos";
import {
  useDeleteComplexLink,
  useGetComplexRelationFields,
  useGetNotRelatedResource,
} from "@/api/related-fields";
import { bibliographyColumnsVisibility } from "@/config/dashboard/bibliography";
import { FormLayoutElement } from "@/config/forms/general";
import { photographiesColumnsVisibility } from "@/config/dashboard/photographies";
import {
  Author,
  Bibliography,
  Photography,
  PhotographyInBibliography,
} from "@/lib/types";
import { ColumnDef, Row, VisibilityState } from "@tanstack/react-table";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useVisibleT } from "@/lib/cms/cms-new-i18n";
import { LinkDrawerRowActions } from "@/components/table/LinkDrawerRowActions";
import { createActionsColumnDef } from "@/components/table/table-actions-column";
import { LinkableType } from "../LinksManagement";
import { HtmlCell, ImageCell, TextCell } from "../table/CustomCell";
import { DataTable } from "../ui/datatable";
import { Input } from "../ui/input";
import { LinksTableProps } from "./ArtworkInAuction";
import { RelatedLinksListGridLayout } from "./RelatedLinksListGridLayout";
import {
  BibliographyLinkGridCard,
  PhotographyLinkGridCard,
} from "./relatedLinkGridCards";
import { GridCardWithActions } from "./GridCardWithActions";
import PhotographyInBibliographyDrawer from "./drawer/PhotographyInBibliographyDrawer";

interface ComplexLinkProps {
  id: string;
  photography: Photography | undefined;
  bibliography: Bibliography | undefined;
  fields: FormLayoutElement[] | undefined;
  type: LinkableType;
}

const columnsForBibliography = [
  {
    accessorKey: "bibliography.bibliographieDescriptionNumeroBibliographie",
    header: "bibliographieDescriptionNumeroBibliographie",
    size: 100,
    cell: (props: any) => {
      return <TextCell value={String(props.getValue() ?? "")} />;
    },
  },
  {
    accessorKey: "vignette",
    header: "",
    size: 100,
    cell: ({ row }: { row: Row<PhotographyInBibliography> }) => {
      const attachments =
        row.original.bibliography?.attachments ??
        row.original.attachments ??
        [];
      return (
        <ImageCell
          value={""}
          attachments={attachments}
          domain={"bibliography"}
        />
      );
    },
  },
  {
    accessorKey: "bibliography.bibliographieDescriptionAnnee",
    header: "bibliographieDescriptionAnnee",
    size: 50,
    cell: (props: any) => {
      return <TextCell value={String(props.getValue() ?? "")} />;
    },
  },
];

const columnsForPhotography = [
  {
    accessorKey: "photography.photosDescriptionNumeroEnregistrement",
    header: "N°",
    enableResizing: true,
    size: 50,
    cell: (props: any) => {
      return <TextCell value={String(props.getValue() ?? "")} />;
    },
  },
  {
    accessorKey: "vignette",
    header: "",
    size: 100,
    cell: ({ row }: { row: Row<PhotographyInBibliography> }) => {
      const attachments =
        row.original.photography?.attachments ?? row.original.attachments ?? [];
      return (
        <ImageCell
          value=""
          attachments={attachments}
          domain="photography"
        />
      );
    },
  },
  {
    accessorKey: "photography.photosDescriptionTitre",
    header: "Titre",
    size: 200,
    cell: (props: any) => {
      return <HtmlCell value={String(props.getValue() ?? "")} />;
    },
  },
  {
    accessorKey: "photography.photosDescriptionTechnique",
    header: "Technique",
    size: 100,
    cell: (props: any) => {
      return <TextCell value={String(props.getValue() ?? "")} />;
    },
  },
  {
    accessorKey: "photography.photosDescriptionDate",
    header: "Date",
    size: 50,
    cell: (props: any) => {
      return <TextCell value={String(props.getValue() ?? "")} />;
    },
  },
];

/**
 * PhotographyInBibliographyLinks component
 * @returns {JSX.Element}
 */
const PhotographyInBibliographyLinks = ({
  id,
  photography,
  fields,
  type,
  bibliography,
}: ComplexLinkProps) => {
  const { t } = useTranslation();
  const vt = useVisibleT();
  const { data: relatedData } = useGetComplexRelationFields(
    id,
    "photographyInBibliography",
    type,
  );

  const { mutate: deletePhotographyInBibliography } = useDeleteComplexLink(
    id,
    "photographyInBibliography",
    type,
  );

  const [searchContent, setSearchContent] = useState("");

  const [isAddMode, setIsAddMode] = useState(false);

  const handleDeleteRelation = useCallback(
    (id: string) => {
      deletePhotographyInBibliography({
        id,
        relation: "photographyInBibliography",
      });
    },
    [deletePhotographyInBibliography],
  );

  const renderPhotographyInBibliographyRowActions = useCallback(
    (data: PhotographyInBibliography) => {
          const preview =
            type === "photography"
              ? {
                  previewType: "bibliography" as const,
                  previewId: data.bibliography.id,
                  previewElements: [
                    {
                      label: t("bibliographieDescriptionNumeroBibliographie"),
                      value:
                        data.bibliography.bibliographieDescriptionNumeroBibliographie,
                      type: "text" as const,
                    },
                    {
                      label: t("bibliographieDescriptionResumeLong"),
                      value: data.bibliography.bibliographieDescriptionResumeLong,
                      type: "richtext" as const,
                    },
                    {
                      label: t("attachments"),
                      value: data.bibliography.attachments,
                      type: "image" as const,
                    },
                  ],
                }
              : type === "bibliography"
                ? {
                    previewType: "photography" as const,
                    previewId: data.photography.id,
                    previewElements: [
                      {
                        label: t("photosDescriptionNumeroEnregistrement"),
                        value: data.photography.photosDescriptionNumeroEnregistrement,
                        type: "text" as const,
                      },
                      {
                        label: t("photosDescriptionLegendeRedigee"),
                        value: data.photography.photosDescriptionLegendeRedigee,
                        type: "richtext" as const,
                      },
                      {
                        label: t("attachments"),
                        value: data.photography.attachments,
                        type: "image" as const,
                      },
                    ],
                  }
                : null;

          if (!preview) return null;

          return (
            <LinkDrawerRowActions
              previewType={preview.previewType}
              previewId={preview.previewId}
              previewElements={preview.previewElements}
              onDelete={() => handleDeleteRelation(data.id)}
              editDrawer={({ open, onOpenChange }) => (
                <PhotographyInBibliographyDrawer
                  key={data.id}
                  fields={fields}
                  queryId={id}
                  type={type}
                  data={data}
                  open={open}
                  onOpenChange={onOpenChange}
                />
              )}
            />
          );
    },
    [t, type, fields, handleDeleteRelation, id],
  );

  const columns: ColumnDef<PhotographyInBibliography, any>[] = useMemo(() => {
    return [
      ...(type === "photography"
        ? columnsForBibliography.map((column) => ({
            ...column,
            header: t(column.header),
          }))
        : columnsForPhotography.map((column) => ({
            ...column,
            header: t(column.header),
          }))),
      {
        accessorKey: "photographiesDansBibliographieDescriptionReproduitPage",
        header: t("photographiesDansBibliographieDescriptionReproduitPage"),
        cell: (props: any) => {
          return <TextCell value={String(props.getValue() ?? "")} />;
        },
      },
      {
        accessorKey: "photographiesDansBibliographieDescriptionCitePage",
        header: t("photographiesDansBibliographieDescriptionCitePage"),
        cell: (props: any) => {
          return <TextCell value={String(props.getValue() ?? "")} />;
        },
      },
      {
        accessorKey:
          "photographiesDansBibliographieDescriptionReproductionCouleur",
        header: t(
          "photographiesDansBibliographieDescriptionReproductionCouleur",
        ),
        size: 50,
        cell: (props: any) => {
          return <TextCell value={props.getValue() ? vt("bo.links.yes", "Oui") : vt("bo.links.no", "Non")} />;
        },
      },
      {
        accessorKey: "photographiesDansBibliographieDescriptionNonPagine",
        header: t("photographiesDansBibliographieDescriptionNonPagine"),
        size: 50,
        cell: (props: any) => {
          return <TextCell value={props.getValue() ? vt("bo.links.yes", "Oui") : vt("bo.links.no", "Non")} />;
        },
      },
      createActionsColumnDef<PhotographyInBibliography>(({ row }) =>
        renderPhotographyInBibliographyRowActions(row.original),
      ),
    ];
  }, [fields, handleDeleteRelation, id, t, type]);

  const datatableConf = useMemo(() => {
    if (type === "photography") {
      return (
        <PhotographyToBibliographyLinks
          id={id}
          relatedData={relatedData}
          fields={fields}
          photography={photography}
          isAddMode={isAddMode}
          columnsLink={columns}
          renderRowActions={renderPhotographyInBibliographyRowActions}
          searchContent={searchContent}
          searchInput={
            <div className="flex items-center gap-2">
              <Input
                placeholder={t("search")}
                className="w-80"
                value={searchContent}
                onChange={(e) => setSearchContent(e.target.value)}
              />
            </div>
          }
          listGridTitle={t("linkedBibliographies")}
          layoutBackLabel={t("linkedEntries")}
          setIsAddMode={setIsAddMode}
        />
      );
    } else {
      return (
        <BibliographyToPhotographyLinks
          id={id}
          relatedData={relatedData}
          fields={fields}
          bibliography={bibliography}
          isAddMode={isAddMode}
          columnsLink={columns}
          renderRowActions={renderPhotographyInBibliographyRowActions}
          searchContent={searchContent}
          searchInput={
            <div className="flex items-center gap-2">
              <Input
                placeholder={t("search")}
                className="w-80"
                value={searchContent}
                onChange={(e) => setSearchContent(e.target.value)}
              />
            </div>
          }
          listGridTitle={t("linkedPhotographies")}
          layoutBackLabel={t("linkedEntries")}
          setIsAddMode={setIsAddMode}
        />
      );
    }
  }, [
    type,
    id,
    relatedData,
    fields,
    photography,
    isAddMode,
    columns,
    searchContent,
    bibliography,
  ]);

  return <div className="w-full">{datatableConf}</div>;
};

type PhotographyToBibliographyLinksProps =
  LinksTableProps<PhotographyInBibliography> & {
    photography: Photography | undefined;
  };

type BibliographyToPhotographyLinksProps =
  LinksTableProps<PhotographyInBibliography> & {
    bibliography: Bibliography | undefined;
  };

//Composant pour Photographies
const PhotographyToBibliographyLinks = ({
  id,
  relatedData,
  fields,
  photography,
  isAddMode,
  columnsLink,
  renderRowActions,
  searchContent,
  searchInput,
  listGridTitle,
  layoutBackLabel,
  setIsAddMode,
}: PhotographyToBibliographyLinksProps) => {
  const { t } = useTranslation();
  const [selectedRelationId, setSelectedRelationId] = useState<string | null>(
    null,
  );
  const [getPagination, setPagination] = useState<PagingParameters>({
    page: 0,
    pageSize: 10,
  });
  const [relatedPagination, setRelatedPagination] =
    useState<PagingParameters>({
      page: 0,
      pageSize: 10,
    });

  useEffect(() => {
    setRelatedPagination((prev) => ({ ...prev, page: 0 }));
  }, [isAddMode]);

  const STORAGE_KEY_ADD = "photography-links-bibliography-add";
  const STORAGE_KEY_LINKED = "photography-links-bibliography-linked";
  const defaultVisibility = { ...bibliographyColumnsVisibility, actions: true };
  const [columnVisibilityAdd, setColumnVisibilityAdd] =
    useState<VisibilityState>(() => {
      try {
        const saved = readTablePreferences(STORAGE_KEY_ADD);
        if (saved) {
          const { visibility } = saved;
          if (visibility && typeof visibility === "object")
            return { ...defaultVisibility, ...visibility };
        }
      } catch {
        // ignore
      }
      return { ...defaultVisibility };
    });
  const [columnVisibilityLinked, setColumnVisibilityLinked] =
    useState<VisibilityState>(() => {
      try {
        const saved = readTablePreferences(STORAGE_KEY_LINKED);
        if (saved) {
          const { visibility } = saved;
          if (visibility && typeof visibility === "object")
            return { ...defaultVisibility, ...visibility };
        }
      } catch {
        // ignore
      }
      return { ...defaultVisibility };
    });
  const [columnsOrderAdd, setColumnsOrderAdd] = useState<string[]>(() => {
    try {
      const saved = readTablePreferences(STORAGE_KEY_ADD);
      if (saved) {
        const { order, columnOrder } = saved;
        const storedOrder = Array.isArray(order)
          ? order
          : Array.isArray(columnOrder)
            ? columnOrder
            : null;
        if (Array.isArray(storedOrder)) {
          const normalizedOrder = [...new Set(storedOrder)] as string[];
          return normalizedOrder.includes("actions")
            ? [...normalizedOrder.filter((id) => id !== "actions"), "actions"]
            : normalizedOrder;
        }
      }
    } catch {
      // ignore
    }
    return [];
  });
  const [columnsOrderLinked, setColumnsOrderLinked] = useState<string[]>(() => {
    try {
      const saved = readTablePreferences(STORAGE_KEY_LINKED);
      if (saved) {
        const { order, columnOrder } = saved;
        const storedOrder = Array.isArray(order)
          ? order
          : Array.isArray(columnOrder)
            ? columnOrder
            : null;
        if (Array.isArray(storedOrder)) {
          const normalizedOrder = [...new Set(storedOrder)] as string[];
          return normalizedOrder.includes("actions")
            ? [...normalizedOrder.filter((id) => id !== "actions"), "actions"]
            : normalizedOrder;
        }
      }
    } catch {
      // ignore
    }
    return [];
  });

  const { data: countByStatus } = useCountByStatus();
  const totalSize = countByStatus?.published + countByStatus?.draft;
  const { data: notRelatedBibliographies } =
    useGetNotRelatedResource<Bibliography>({
      resultType: "bibliography",
      resourceId: id,
      pagination: getPagination,
      searchQuery: searchContent,
    });

  const columnsBibliographies: ColumnDef<Bibliography, any>[] = useMemo(() => {
    return [
      {
        accessorKey: "bibliographieDescriptionNumeroBibliographie",
        header: t("bibliographieDescriptionNumeroBibliographie"),
        size: 50,
        cell: (props) => {
          return <TextCell value={String(props.getValue() ?? "")} />;
        },
      },
      {
        accessorKey: "vignette",
        header: "",
        size: 100,
        cell: ({ row }: { row: Row<Bibliography> }) => {
          return (
            <ImageCell
              value={""}
              attachments={row.original.attachments ?? []}
              domain={"bibliography"}
            />
          );
        },
      },
      {
        accessorKey: "bibliographieDescriptionAnnee",
        header: t("bibliographieDescriptionAnnee"),
        size: 100,
        cell: (props) => {
          return <TextCell value={String(props.getValue() ?? "")} />;
        },
      },
      {
        accessorKey: "relatedAuthor",
        header: t("bibliographieDescriptionAuteur"),
        size: 200,
        cell: (props: any) => {
          return (
            <TextCell
              value={props.row.original.relatedAuthor
                .map((author: Author) => author.artistesAuteursDescriptionNom)
                .join(", ")}
            />
          );
        },
      },
      createActionsColumnDef<Bibliography>((props) => {
          const data = props.row.original as Bibliography;
          if (!photography) return null;
          return (
            <LinkDrawerRowActions
              previewType="bibliography"
              previewId={props.row.original.id}
              previewElements={[
                {
                  label: t("bibliographieDescriptionNumeroBibliographie"),
                  value: data.bibliographieDescriptionNumeroBibliographie,
                  type: "text",
                },
                {
                  label: t("bibliographieDescriptionResumeLong"),
                  value: data.bibliographieDescriptionResumeLong,
                  type: "richtext",
                },
                {
                  label: t("attachments"),
                  value: data.attachments,
                  type: "image",
                },
              ]}
              editLabel={t("actions.add")}
              editDrawer={({ open, onOpenChange }) => (
                <PhotographyInBibliographyDrawer
                  key={props.row.original.id}
                  fields={fields}
                  queryId={photography.id}
                  type="photography"
                  open={open}
                  onOpenChange={onOpenChange}
                  data={{
                    id: "",
                    bibliography: data,
                    photography: photography,
                    photographiesDansBibliographieDescriptionReproduitPage: "",
                    photographiesDansBibliographieDescriptionCitePage: "",
                    photographiesDansBibliographieDescriptionPhotographe: "",
                    photographiesDansBibliographieDescriptionReproductionCouleur:
                      false,
                    photographiesDansBibliographieDescriptionNonPagine: false,
                  }}
                />
              )}
            />
          );
        }),
    ];
  }, [fields, photography, t]);

  const paginatedRelatedData = useMemo(() => {
    const start = relatedPagination.page * relatedPagination.pageSize;
    const end = start + relatedPagination.pageSize;
    return (relatedData ?? []).slice(start, end);
  }, [relatedData, relatedPagination.page, relatedPagination.pageSize]);

  const relatedPaginationProps = {
    pagination: relatedPagination,
    setPage: setRelatedPagination,
    totalSize: (relatedData ?? []).length,
  };
  const addModeTable = (
    <DataTable columns={columnsBibliographies} data={notRelatedBibliographies?.data ?? []} pagination={{ pagination: getPagination, setPage: setPagination, totalSize: totalSize }} storageKey={STORAGE_KEY_ADD} options={{ enableColumnResizing: true, onColumnVisibilityChange: setColumnVisibilityAdd, onColumnOrderChange: setColumnsOrderAdd, state: { columnVisibility: columnVisibilityAdd, columnOrder: columnsOrderAdd } }} />
  );
  const listTable = (
    <DataTable columns={columnsLink} data={paginatedRelatedData} pagination={relatedPaginationProps} storageKey={STORAGE_KEY_LINKED} options={{ enableColumnResizing: true, onColumnVisibilityChange: setColumnVisibilityLinked, onColumnOrderChange: setColumnsOrderLinked, state: { columnVisibility: columnVisibilityLinked, columnOrder: columnsOrderLinked } }} />
  );
  if (listGridTitle && setIsAddMode && searchInput) {
    return (
      <RelatedLinksListGridLayout
        storageKey={STORAGE_KEY_LINKED}
        title={listGridTitle}
        backLabel={layoutBackLabel}
        isAddMode={isAddMode}
        onBack={() => setIsAddMode(false)}
        onToggleAdd={() => setIsAddMode(true)}
        searchInput={searchInput}
        addModeTable={addModeTable}
        listTable={listTable}
        gridContent={
          <>
            {paginatedRelatedData.map((row) => (
              <div key={row.id}>
                <GridCardWithActions actions={renderRowActions?.(row)}>
                  <BibliographyLinkGridCard
                  item={row.bibliography}
                  t={t}
                  onTextClick={() => setSelectedRelationId(row.id)}
                />
                </GridCardWithActions>
                                <PhotographyInBibliographyDrawer
                  fields={fields}
                  data={row}
                  queryId={id}
                  type="photography"
                  open={selectedRelationId === row.id}
                  onOpenChange={(open) => {
                    if (!open) setSelectedRelationId(null);
                  }}
                />
              </div>
            ))}
          </>
        }
        relatedPagination={relatedPaginationProps}
      />
    );
  }
  return isAddMode ? addModeTable : listTable;
};

// Composant pour Bibliographies
const BibliographyToPhotographyLinks = ({
  id,
  relatedData,
  fields,
  bibliography,
  isAddMode,
  columnsLink,
  renderRowActions,
  searchContent,
  searchInput,
  listGridTitle,
  layoutBackLabel,
  setIsAddMode,
}: BibliographyToPhotographyLinksProps) => {
  const { t } = useTranslation();
  const [selectedRelationId, setSelectedRelationId] = useState<string | null>(
    null,
  );
  const [getPagination, setPagination] = useState<PagingParameters>({
    page: 0,
    pageSize: 10,
  });
  const [relatedPagination, setRelatedPagination] =
    useState<PagingParameters>({
      page: 0,
      pageSize: 10,
    });

  useEffect(() => {
    setRelatedPagination((prev) => ({ ...prev, page: 0 }));
  }, [isAddMode]);

  const STORAGE_KEY_ADD_P = "bibliography-links-photography-add";
  const STORAGE_KEY_LINKED_P = "bibliography-links-photography-linked";
  const defaultVisibilityP = {
    ...photographiesColumnsVisibility,
    actions: true,
  };
  const [columnVisibilityAddP, setColumnVisibilityAddP] =
    useState<VisibilityState>(() => {
      try {
        const saved = readTablePreferences(STORAGE_KEY_ADD_P);
        if (saved) {
          const { visibility } = saved;
          if (visibility && typeof visibility === "object")
            return { ...defaultVisibilityP, ...visibility };
        }
      } catch {
        // ignore
      }
      return { ...defaultVisibilityP };
    });
  const [columnVisibilityLinkedP, setColumnVisibilityLinkedP] =
    useState<VisibilityState>(() => {
      try {
        const saved = readTablePreferences(STORAGE_KEY_LINKED_P);
        if (saved) {
          const { visibility } = saved;
          if (visibility && typeof visibility === "object")
            return { ...defaultVisibilityP, ...visibility };
        }
      } catch {
        // ignore
      }
      return { ...defaultVisibilityP };
    });
  const [columnsOrderAddP, setColumnsOrderAddP] = useState<string[]>(() => {
    try {
      const saved = readTablePreferences(STORAGE_KEY_ADD_P);
      if (saved) {
        const { order, columnOrder } = saved;
        const storedOrder = Array.isArray(order)
          ? order
          : Array.isArray(columnOrder)
            ? columnOrder
            : null;
        if (Array.isArray(storedOrder)) {
          const normalizedOrder = [...new Set(storedOrder)] as string[];
          return normalizedOrder.includes("actions")
            ? [...normalizedOrder.filter((id) => id !== "actions"), "actions"]
            : normalizedOrder;
        }
      }
    } catch {
      // ignore
    }
    return [];
  });
  const [columnsOrderLinkedP, setColumnsOrderLinkedP] = useState<string[]>(
    () => {
      try {
        const saved = readTablePreferences(STORAGE_KEY_LINKED_P);
        if (saved) {
          const { order, columnOrder } = saved;
          const storedOrder = Array.isArray(order)
            ? order
            : Array.isArray(columnOrder)
              ? columnOrder
              : null;
          if (Array.isArray(storedOrder)) {
            const normalizedOrder = [...new Set(storedOrder)] as string[];
            return normalizedOrder.includes("actions")
              ? [
                  ...normalizedOrder.filter((id) => id !== "actions"),
                  "actions",
                ]
              : normalizedOrder;
          }
        }
      } catch {
        // ignore
      }
      return [];
    },
  );

  const { data: notRelatedPhotographies } =
    useGetNotRelatedResource<Photography>({
      resultType: "photography",
      resourceId: id,
      pagination: getPagination,
      searchQuery: searchContent,
    });

  const columnsDocuments: ColumnDef<Photography, any>[] = useMemo(() => {
    return [
      {
        accessorKey: "photosDescriptionNumeroEnregistrement",
        header: t("photosDescriptionNumeroEnregistrement"),
        enableResizing: true,
        size: 50,
        cell: (props) => {
          return <TextCell value={String(props.getValue() ?? "")} />;
        },
      },
      {
        accessorKey: "vignette",
        header: "",
        size: 100,
        cell: ({ row }: { row: Row<Photography> }) => {
          return (
            <ImageCell
              value={""}
              attachments={row.original.attachments ?? []}
              domain={"photography"}
            />
          );
        },
      },
      {
        accessorKey: "photosDescriptionTitre",
        header: t("photosDescriptionTitre"),
        size: 200,
        cell: (props) => {
          return <HtmlCell value={String(props.getValue() ?? "")} />;
        },
      },
  {
    accessorKey: "photosDescriptionTechnique",
    header: t("photosDescriptionTechnique"),
    size: 100,
    cell: (props) => {
      return <TextCell value={String(props.getValue() ?? "")} />;
    },
  },
  {
    accessorKey: "photosDescriptionDate",
        header: t("photosDescriptionDate"),
        size: 100,
        cell: (props) => {
          return <TextCell value={String(props.getValue() ?? "")} />;
        },
      },
      createActionsColumnDef<Photography>((props) => {
          const data = props.row.original as Photography;
          if (!bibliography) return null;
          return (
            <LinkDrawerRowActions
              previewType="photography"
              previewId={props.row.original.id}
              previewElements={[
                {
                  label: t("photosDescriptionNumeroEnregistrement"),
                  value: data.photosDescriptionNumeroEnregistrement,
                  type: "text",
                },
                {
                  label: t("photosDescriptionLegendeRedigee"),
                  value: data.photosDescriptionLegendeRedigee,
                  type: "richtext",
                },
                {
                  label: t("attachments"),
                  value: data.attachments,
                  type: "image",
                },
              ]}
              editLabel={t("actions.add")}
              editDrawer={({ open, onOpenChange }) => (
                <PhotographyInBibliographyDrawer
                  key={props.row.original.id}
                  fields={fields}
                  queryId={bibliography.id}
                  type="bibliography"
                  open={open}
                  onOpenChange={onOpenChange}
                  data={{
                    id: "",
                    bibliography: bibliography,
                    photography: data,
                    photographiesDansBibliographieDescriptionReproduitPage: "",
                    photographiesDansBibliographieDescriptionCitePage: "",
                    photographiesDansBibliographieDescriptionPhotographe: "",
                    photographiesDansBibliographieDescriptionReproductionCouleur:
                      false,
                    photographiesDansBibliographieDescriptionNonPagine: false,
                  }}
                />
              )}
            />
          );
        }),
    ];
  }, [bibliography, fields, t]);

  const paginatedRelatedData = useMemo(() => {
    const start = relatedPagination.page * relatedPagination.pageSize;
    const end = start + relatedPagination.pageSize;
    return (relatedData ?? []).slice(start, end);
  }, [relatedData, relatedPagination.page, relatedPagination.pageSize]);

  const relatedPaginationProps = {
    pagination: relatedPagination,
    setPage: setRelatedPagination,
    totalSize: (relatedData ?? []).length,
  };
  const addModeTable = (
    <DataTable columns={columnsDocuments} data={notRelatedPhotographies?.data ?? []} pagination={{ pagination: getPagination, setPage: setPagination, totalSize: notRelatedPhotographies?.totalSize ?? 0 }} storageKey={STORAGE_KEY_ADD_P} options={{ enableColumnResizing: true, onColumnVisibilityChange: setColumnVisibilityAddP, onColumnOrderChange: setColumnsOrderAddP, state: { columnVisibility: columnVisibilityAddP, columnOrder: columnsOrderAddP } }} />
  );
  const listTable = (
    <DataTable columns={columnsLink} data={paginatedRelatedData} pagination={relatedPaginationProps} storageKey={STORAGE_KEY_LINKED_P} options={{ enableColumnResizing: true, onColumnVisibilityChange: setColumnVisibilityLinkedP, onColumnOrderChange: setColumnsOrderLinkedP, state: { columnVisibility: columnVisibilityLinkedP, columnOrder: columnsOrderLinkedP } }} />
  );
  if (listGridTitle && setIsAddMode && searchInput) {
    return (
      <RelatedLinksListGridLayout
        storageKey={STORAGE_KEY_LINKED_P}
        title={listGridTitle}
        backLabel={layoutBackLabel}
        isAddMode={isAddMode}
        onBack={() => setIsAddMode(false)}
        onToggleAdd={() => setIsAddMode(true)}
        searchInput={searchInput}
        addModeTable={addModeTable}
        listTable={listTable}
        gridContent={
          <>
            {paginatedRelatedData.map((row) => (
              <div key={row.id}>
                <GridCardWithActions actions={renderRowActions?.(row)}>
                  <PhotographyLinkGridCard
                  item={row.photography}
                  t={t}
                  onTextClick={() => setSelectedRelationId(row.id)}
                />
                </GridCardWithActions>
                                <PhotographyInBibliographyDrawer
                  fields={fields}
                  data={row}
                  queryId={id}
                  type="bibliography"
                  open={selectedRelationId === row.id}
                  onOpenChange={(open) => {
                    if (!open) setSelectedRelationId(null);
                  }}
                />
              </div>
            ))}
          </>
        }
        relatedPagination={relatedPaginationProps}
      />
    );
  }
  return isAddMode ? addModeTable : listTable;
};

export default PhotographyInBibliographyLinks;
